import {combineReducers} from 'redux'
import reduceReducers from 'reduce-reducers'
import * as types from './actionTypes.js'

import {
    selectIsFilterActive,
    selectFilterById,
    selectFilterChildren,
    selectFilterParent,
    selectActiveFilters,
    selectFilterType,
    selectFiltersByMulti,
    selectExclusiveFilterChangeHash,
    selectSingleFilterChangeHash,
    selectMultiFilterChangeHash
} from './selectors.js'


/*----------------------------------------
 * TOP REDUCERS 
 */

/*
 * Combine reducers that opperate on isolated parts of state tree
 */
export const combinedReducer = combineReducers({
    ui : uiReducer,
    config : configReducer,
    data : dataReducer,
});

/*
 * Stack reducers that operate at the root of the state tree
 */
export const rootReducer = reduceReducers(
    combinedReducer,
    topReducer
)


/*----------------------------------------
 * HELPER FUNCTIONS
 */
function buildKeywordFilter (text) {
    return {
        id : `keyword-text-${text}`,
        preposition : "matching",
        value : text,
        name : text,
        context: "keyword",
        param : "keywords",
        paramName : "Keyword",
        type: "single"
    }
}


/*----------------------------------------
 * REDUCER FUNCTIONS
 */
function configReducer (state = {}) {
    return state 
}


/*
 * reducers that need to access multiple areas of the state tree 
 * neet to live in the top reducer. 
 *
 * This includes changes to ativeFilters that need to access the
 * param config values
 */

const topFilterContextClear = (state, payload) => {
    var contextId = payload
    var changeHash = {}
    var activeFilters = selectActiveFilters(state)

    activeFilters.forEach( f => {
        f.context === contextId ? changeHash[f.id] = false : null
    })

    return {
        ...state, 
        data : {
            ...state.data,
            activeFilters : {
                ...state.data.activeFilters,
                ...changeHash
            }
        }
    }
}

/*
 * This is the most complex bit of the whole interface
 * Deciding what to happen when a filter is toggled is complicated 
 * by nesting, and wheater a filter is exclusive, single, or multi
 */
const topFilterToggle = (state, payload) => {
    var filterId = payload
    var filter = selectFilterById(state, filterId)

    var filterMightBeParent = filter.parentId ? false : true
    var filterHasParent = !filterMightBeParent
    var filterIsActive = selectIsFilterActive(state, filterId)

    var changeHash = {}

    var activeFilters = selectActiveFilters(state)
    if (activeFilters.length === 1 && selectFilterType(state, activeFilters[0].id) === "exclusive" ) {
        changeHash[activeFilters[0].id] = false
    }
    /* Cases
     * 1. not nested
     *      a. is exclusive 
     *      b. is single
     *      c. is multi
     * 2. is a parent (and multi)
     * 3. is a child (and multi)
     *      a. parent is checked
     *      b. parent is unchecked
     *          i. self is checked
     *          ii. self is unchecked
     *              (1). all siblings are checked
     *              (2). not all siblings are checked
     */

    if (filterMightBeParent) {
        var children = selectFilterChildren(state, filterId)

        if ( children.length === 0 ) {
            /*
             * 1. not "nested" filter => toggle only self 
             */
            var filterType = filter.type



            if (filterType === "exclusive") {
                changeHash = {
                    ...changeHash,
                    ...selectExclusiveFilterChangeHash(state, filterId)
                }
            }
            else if (filterType === "single") {
                changeHash = {
                    ...changeHash,
                    ...selectSingleFilterChangeHash(state, filterId)
                }
            }
            else {
                changeHash = {
                    ...changeHash,
                    ...selectMultiFilterChangeHash(state, filterId)
                }
            }
        } 
        else {
            /* 
             * 2. is a parent
             */

            // => unactivate all children 
            for (let child of children) {
                changeHash[child.id] = false
            }

            // => toggle the parent
            changeHash[filterId] = filterIsActive ? false : true
        }
    } 
    else if (filterHasParent) {
        /* 
         * 3. is a child 
         */
        var parentFilter = selectFilterParent(state, filterId)
            var parentIsActive = selectIsFilterActive(state, parentFilter.id)
            var siblings = selectFilterChildren(state, parentFilter.id)
            .filter( s => s.id !== filterId )

        if (parentIsActive) {
            /*
             * a. parent is checked
             */

            // => uncheck parent
            changeHash[parentFilter.id] = false 
            // => make sure self is unchecked
            changeHash[filterId] = false
            // => check all siblings
            siblings.forEach( (s) => changeHash[s.id] = true )
        } 
        else {
            /*
             * b. parent is unchecked
             */

            if (filterIsActive) {
                /*
                 * i. self is checked 
                 */

                // => uncheck self
                changeHash[filterId] = false
            } 
            else {
                /*
                 * ii. self is unchecked
                 */
                var allSiblingsSelected = siblings.reduce( (acc, s) => {
                        return acc && selectIsFilterActive(state, s.id)
                        }, true)

                if (allSiblingsSelected) {
                    /*
                     * (1). of children only self is unchecked
                     */

                    // => check parent
                    changeHash[parentFilter.id] = true
                    // => uncheck all siblings
                    siblings.forEach( (s) => changeHash[s.id] = false )
                    // => uncheck self
                    changeHash[filterId] = false
                } 
                else {
                    /*
                     * (2). some number 0 to N-1 of siblings are checked
                     */

                    changeHash[filterId] = true 
                }
            }
        }
    }

    return {
        ...state, 
        data : {
            ...state.data,
            activeFilters : {
                ...state.data.activeFilters,
                ...changeHash
            }
        }
    }
}


const topFilterGroupDeactivate = (state, payload) => { 
    // storing the active filters by id makes this a bit more complex, 
    var paramName = payload
    var activeFilterList = [...selectActiveFilters(state)]

    var newActiveFilters = {}
    var newUI = {}
    activeFilterList.forEach( filter => {
        if ( filter.param === "keywords" ) {
          newUI.keywordText = ""
        }
        if ( filter.paramName !== paramName ) {
            newActiveFilters[filter.id] = true
        }
    })

    return {
        ...state,
        ui : {
          ...state.ui,
          ...newUI
        },
        data : {
            ...state.data, 
            activeFilters : newActiveFilters
        }
    }
}


const topKeywordTextSearch = (state, payload) => { 
    var keywordText = payload
    var keywordFilter = buildKeywordFilter(keywordText)
    var filters = state.data.filters
    var activeFilters = selectActiveFilters(state)

    var changeHash = {}

    // remove any active keyword filters
    activeFilters.forEach( f => {
        if ( f.paramName === "Keyword" ) {
            changeHash[f.id] = false
        }
    })
    changeHash[keywordFilter.id] = true

    var newFilters = filters.filter( f => f.param !== "keywords" ) 

    return {
        ...state,
        data : {
            ...state.data, 
            filters : [...newFilters, keywordFilter],
            activeFilters : {
                ...state.data.activeFilters,
                ...changeHash
            },
        }
    }
}

const topFilterPush = (state, payload) => { 
  var filter = payload
  var filters = state.data.filters
  var activeFilters = selectActiveFilters(state)


  var newFilters = [...filters]

  let index = filters.findIndex( f => {
    return f.param === filter.param
  })
  if (index < 0) {
    newFilters.push(filter)
  } 
  else {
    newFilters.splice(index, 1, filter)
  }

  var changeHash = {}

  // remove any active filters matching param
  activeFilters.forEach( f => {
    if ( f.param === filter.param ) {
      changeHash[f.id] = false
    }
  })
  changeHash[filter.id] = true

  return {
    ...state,
    data : {
      ...state.data, 
      filters : newFilters,
      activeFilters : {
        ...state.data.activeFilters,
        ...changeHash
      },
    }
  }
}

const topFilterReplace = (state, payload) => { 
  state = topFilterGroupDeactivate(state, payload.param)

  var filter = payload
  var filters = state.data.filters
  var activeFilters = selectActiveFilters(state)

  var newFilters = [...filters]

  let index = filters.findIndex( f => {
    return f.param === filter.param
  })
  if (index < 0) {
    newFilters.push(filter)
  } 
  else {
    newFilters.splice(index, 1, filter)
  }

  var changeHash = {}

  // remove any active filters matching param
  activeFilters.forEach( f => {
    if ( f.param === filter.param ) {
      changeHash[f.id] = false
    }
  })
  changeHash[filter.id] = true

  return {
    ...state,
    data : {
      ...state.data, 
      filters : newFilters,
      activeFilters : {
        ...state.data.activeFilters,
        ...changeHash
      },
    }
  }
}




const topKeywordMatchComplete = (state, payload) => { 
    var filterId = payload
    var filter = selectFilterById(state, filterId)
    var changeHash = {}

    var filterType = filter.type

    // xxx check to see if the current active filter is exclusive. 
    // if so, it should be removed when another filter is chosen. 

    var activeFilters = selectActiveFilters(state)



    if (filterType === "exclusive") {
        changeHash = selectExclusiveFilterChangeHash(state, filterId)
    }
    else if (filterType === "single") {
        changeHash = selectSingleFilterChangeHash(state, filterId)
    }
    else {
        changeHash = selectMultiFilterChangeHash(state, filterId)
    }

    if (activeFilters.length > 0 && selectFilterType(state, activeFilters[0].id) === "exclusive" ) {
        changeHash[activeFilters[0].id] = false
    }

    return {
        ...state, 
        ui : {
          ...state.ui,
          keywordText: ""
        },
        data : {
            ...state.data,
            activeFilters : {
                ...state.data.activeFilters,
                ...changeHash
            }
        }
    }
}


function topReducer (state = {}, action) {
  var { type, payload } = action

  switch (type) {
    case types.FILTER_CONTEXT_DEACTIVATE:
      return topFilterContextClear(state, payload)

    case types.FILTER_TOGGLE:
      return topFilterToggle(state, payload)

    case types.FILTER_PUSH:
      return topFilterPush(state, payload)

    case types.FILTER_REPLACE:
      return topFilterReplace(state, payload)

    case types.FILTER_GROUP_DEACTIVATE:
      return topFilterGroupDeactivate(state, payload)

    case types.KEYWORD_TEXT_SEARCH:
      return topKeywordTextSearch(state, payload)

    case types.KEYWORD_MATCH_COMPLETE:
      return topKeywordMatchComplete(state, payload)

    default: 
      return state
  }
}


const dataPostInitDataReceive = (state, payload) => {
  var url = new URL(window.location.href) 
  var params = url.searchParams

  var paramList = []
  for ( let p of params.entries() ) {
    var param = p[0]
    let value = p[1]
    var values = value.split(',')

    for ( let v of values ) {
      paramList.push({ param, value: v })
    }
  }

  /* need to merge payload data with seedFilter data */
  var existingFilters = state.filters
  
    var newFilters = [...existingFilters]

    for ( let filter of payload ) {
      let index = newFilters.findIndex( f => {
        return f.param === filter.param && f.value === filter.value
      })
      if (index < 0) {
        newFilters.push(filter)
      } 
      else {
        newFilters.splice(index, 1, filter)
      }
    }

    var data = newFilters 

    var filterChangeHash = {} 
    // var activeSort
    for ( let param of paramList ) {
        // var value = params[param]
        if (param === "sortType") {
            // activeSort = value
            // console.log("Sort Type Debug", param)
        }
        else {
            var targetFilters = selectFiltersByMulti({ data : { filters : data }}, param)

            targetFilters.length ? filterChangeHash[targetFilters[0].id] = true : null
        }
    }

    return {
        ...state,
        filters : data,
        activeFilters : filterChangeHash
    }
}


const dataKeywordDataRecieve = (state, payload) => {
    var keywordData = payload

    var filters = []
    for ( let group of keywordData ) {
        for ( let ref of group.values ) {
            var name = ref.name ? ref.name : ref.value
            var value = ref.value

            filters.push({
                id : `${group.context}-${value}`,
                context : group.context,
                param : group.param,
                paramName : group.paramName,
                preposition: group.preposition,
                type: group.type,
                name, 
                value
            })
        }
    }

    return {
        ...state,
        filters : [...state.filters, ...filters]
    }
}


function dataReducer (state = {}, action) {
    var { type, payload } = action

    switch (type) {
        case types.POST_INIT_DATA_RECEIVE:
            return dataPostInitDataReceive(state, payload)

        case types.KEYWORD_DATA_RECEIVE:
            return dataKeywordDataRecieve(state, payload)

        case types.SORT_CHANGE:
            var newSortValue = payload
            return {
                ...state,
                activeSort : newSortValue,
            }

        case types.ALL_FILTERS_DEACTIVATE:
            return {
                ...state,
                activeFilters : [],
            }

        default:
            return state
    }
}


const uiModalOpen = (state, payload) => {
    var modalId = payload
    var lastModal = modalId !== "keyword" && modalId !== "sort" ? modalId : state.lastModal

    return  {
        ...state,
        modalId,
        lastModal
    }
}


const uiLastModalOpen = (state) => {
    var modalId = state.lastModal
    return {
        ...state,
        modalId,
    }
}


const uiNestingToggle = (state, payload) => {
    var filterId = payload 
    return {
        ...state,
        openNestedLists : {
            ...state.openNestedLists,
            [filterId] : state.openNestedLists[filterId] ? false : true
        },
    }
}


export function uiReducer (state = {}, action) {
    var { type, payload } = action

    switch (type) {
        case types.MODAL_OPEN:
            return uiModalOpen(state, payload) 

        case types.LAST_MODAL_OPEN:
            return uiLastModalOpen(state, payload)

        case types.MODAL_CLOSE:
            return {
                ...state,
                modalId : null,
            }

        case types.NESTING_TOGGLE:
            return uiNestingToggle(state, payload)

        case types.KEYWORD_TEXT_CHANGE:
            var keywordText = payload

            return {
                ...state,
                keywordText
            }

        default:
            return state
    }
}
